import { useState, useEffect } from 'react';
import { getDocs, query, orderBy, collectionGroup } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CodeUsageHistory } from '@/types';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { History, Gift, Ticket, Search, Filter, Download } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const AdminCodeHistory = () => {
  const [history, setHistory] = useState<CodeUsageHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'invite' | 'campaign'>('all');

  useEffect(() => {
    fetchAllHistory();
  }, []);

  const fetchAllHistory = async () => {
    try {
      setLoading(true);
      // collectionGroupを使用して全ユーザーのcodeUsageHistoryを取得
      const historyQuery = query(
        collectionGroup(db, 'codeUsageHistory'),
        orderBy('appliedAt', 'desc')
      );
      
      const snapshot = await getDocs(historyQuery);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as CodeUsageHistory[];
      
      setHistory(data);
    } catch (error) {
      console.error('Failed to fetch code usage history:', error);
      alert('コード使用履歴の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // フィルタリングされた履歴
  const filteredHistory = history.filter((item) => {
    const matchesSearch = 
      item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.userEmail.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = 
      filterType === 'all' ||
      item.codeType === filterType;
    
    return matchesSearch && matchesType;
  });

  // 統計情報
  const stats = {
    total: history.length,
    invite: history.filter((h) => h.codeType === 'invite').length,
    campaign: history.filter((h) => h.codeType === 'campaign').length,
  };

  const exportToCSV = () => {
    const headers = ['適用日時', 'コードタイプ', 'コード', 'ユーザー名', 'メールアドレス', 'プラン変更', '付与日数'];
    const rows = filteredHistory.map((item) => [
      format(item.appliedAt.toDate(), 'yyyy/MM/dd HH:mm:ss', { locale: ja }),
      item.codeType === 'invite' ? '招待コード' : 'キャンペーンコード',
      item.code,
      item.userName,
      item.userEmail,
      `${item.planBefore} → ${item.planAfter}`,
      item.rewardDays ? `${item.rewardDays}日` : '-',
    ]);
    
    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `code_usage_history_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ページヘッダー */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">コード使用履歴</h1>
            <p className="text-gray-600 mt-1">全ユーザーのコード適用履歴を確認</p>
          </div>
          
          <button
            onClick={exportToCSV}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            CSV出力
          </button>
        </div>

        {/* 統計カード */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <History className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-600">総利用回数</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Gift className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">招待コード</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.invite}</p>
          </div>

          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Ticket className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-gray-600">キャンペーンコード</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.campaign}</p>
          </div>
        </div>

        {/* フィルター */}
        <div className="card">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="コード、ユーザー名、メールアドレスで検索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as typeof filterType)}
                className="input"
              >
                <option value="all">すべて</option>
                <option value="invite">招待コードのみ</option>
                <option value="campaign">キャンペーンのみ</option>
              </select>
            </div>
          </div>
        </div>

        {/* 履歴テーブル */}
        <div className="card">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            利用履歴一覧 ({filteredHistory.length}件)
          </h2>
          
          {filteredHistory.length === 0 ? (
            <div className="h-96 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">該当する履歴がありません</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3">適用日時</th>
                    <th scope="col" className="px-6 py-3">タイプ</th>
                    <th scope="col" className="px-6 py-3">コード</th>
                    <th scope="col" className="px-6 py-3">ユーザー</th>
                    <th scope="col" className="px-6 py-3">プラン変更</th>
                    <th scope="col" className="px-6 py-3">終了日</th>
                    <th scope="col" className="px-6 py-3">付与日数</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((item) => (
                    <tr key={item.id} className="bg-white border-b hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(item.appliedAt.toDate(), 'yyyy/MM/dd HH:mm', { locale: ja })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.codeType === 'invite'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {item.codeType === 'invite' ? (
                            <Gift className="w-3 h-3 mr-1" />
                          ) : (
                            <Ticket className="w-3 h-3 mr-1" />
                          )}
                          {item.codeType === 'invite' ? '招待' : 'キャンペーン'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          {item.code}
                        </code>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{item.userName}</div>
                          <div className="text-gray-500">{item.userEmail}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="text-gray-600">{item.planBefore}</span>
                        <span className="mx-2">→</span>
                        <span className="font-semibold text-green-600">{item.planAfter}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {item.endDateAfter ? (
                          format(item.endDateAfter.toDate(), 'yyyy/MM/dd', { locale: ja })
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {item.rewardDays !== undefined ? (
                          <span className="font-semibold text-blue-600">{item.rewardDays}日</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
